/**
 * Manage Passkeys dialog — accessed from the user menu. Lets the signed-in
 * user register a new passkey on this device and revoke any of their
 * existing ones. One row per device.
 */
import { useEffect, useState } from "react";
import { Fingerprint, Trash2, Plus, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  listMyPasskeys,
  registerPasskey,
  revokeMyPasskey,
  passkeysSupported,
  platformAuthenticatorAvailable,
  markPasskeyRegistered,
  clearLocalPasskeyHint,
} from "@/lib/passkeys";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function ManagePasskeysDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setSupported(passkeysSupported() && (await platformAuthenticatorAvailable()));
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const load = async () => {
    setLoading(true);
    try {
      const list = await listMyPasskeys();
      setPasskeys(list);
      if (list.length > 0) markPasskeyRegistered();
      else clearLocalPasskeyHint();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load passkeys");
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async () => {
    setRegistering(true);
    try {
      await registerPasskey();
      markPasskeyRegistered();
      toast.success("Passkey saved on this device");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      if (detail && !/cancelled/i.test(detail)) toast.error(detail);
    } finally {
      setRegistering(false);
    }
  };

  const onRevoke = async (id) => {
    try {
      await revokeMyPasskey(id);
      toast.success("Passkey removed");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke passkey");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="manage-passkeys-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Fingerprint className="h-5 w-5 text-[#C9A84C]" />
            Passkeys
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Save your account on this device so you can sign in with Face ID,
            Touch ID, or Windows Hello — no more typing your code and PIN.
          </p>

          {!supported && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              Your browser does not support passkeys on this device.
            </div>
          )}

          <div className="space-y-2" data-testid="passkey-list">
            {loading ? (
              <div className="text-sm text-gray-500">Loading…</div>
            ) : passkeys.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 p-4 text-center text-sm text-gray-500">
                No passkeys yet.
              </div>
            ) : (
              passkeys.map((pk) => (
                <div
                  key={pk.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3"
                  data-testid={`passkey-row-${pk.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-5 w-5 text-[#1A2744]" />
                    <div>
                      <div className="text-sm font-semibold text-[#1A2744]">
                        {pk.device_label}
                      </div>
                      <div className="text-xs text-gray-500">
                        Added {formatDate(pk.created_at)}
                        {pk.last_used_at ? ` · Last used ${formatDate(pk.last_used_at)}` : ""}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRevoke(pk.id)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`revoke-passkey-${pk.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            onClick={onRegister}
            disabled={!supported || registering}
            className="w-full bg-[#1A2744] hover:bg-[#263A63] text-white"
            data-testid="add-passkey-button"
          >
            <Plus className="h-4 w-4 mr-1" />
            {registering ? "Waiting for device…" : "Add passkey on this device"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
